import { useEffect, useMemo, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Box,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { ListToolbar, useListContext } from 'react-admin';

import type { Receipt, ResponsibleUser } from './receiptApi';
import {
    statusColor,
    allocationColor,
    localToday,
    type QuickRange,
    quickDateRange,
} from './receiptHelpers';
import { receiptDateRangeFilters } from './ReceiptDateRangeFilters';
import {
    FilterSection,
    QUICK_RANGES,
    type ActiveFilterChip,
    type QuickRangeOption,
    ReceiptFiltersAccordion,
    QuickRangeButtons,
    quickRangeLabel,
    ClearFiltersButton,
} from './ReceiptFilterParts';

export const DAILY_STICKY_KEYS = ['deleted_at'];

const RECEIPT_STATUS_OPTIONS = [
    ['unmatched', 'Unmatched'],
    ['matched', 'Matched'],
    ['discrepancy', 'Discrepancy'],
] as const;

const ALLOCATION_STATUS_OPTIONS = [
    ['pending', 'Pending'],
    ['verified', 'Verified'],
    ['needs_correction', 'Needs correction'],
] as const;

/**
 * buildDailyActiveFilterChips: creates active filter chips for Daily receipts filters.
 * Exported for testing.
 */
export const buildDailyActiveFilterChips = (
    filterValues: Record<string, unknown> | undefined,
    users: ResponsibleUser[],
    quickRangeOptions: QuickRangeOption[]
): ActiveFilterChip[] => {
    const chips: ActiveFilterChip[] = [];

    // Search query
    if (filterValues?.q) {
        chips.push({
            id: 'q',
            label: `"${filterValues.q}"`,
            color: 'default',
        });
    }

    // Receipt status
    if (filterValues?.status) {
        const status = filterValues.status as string;
        const label = RECEIPT_STATUS_OPTIONS.find(([val]) => val === status)?.[1] || status;
        chips.push({
            id: 'status',
            label,
            color: statusColor(status),
        });
    }

    // Allocation status
    if (filterValues?.allocation_status) {
        const allocationStatus = filterValues.allocation_status as string;
        const label = ALLOCATION_STATUS_OPTIONS.find(
            ([val]) => val === allocationStatus
        )?.[1] || allocationStatus;
        chips.push({
            id: 'allocation_status',
            label,
            color: allocationColor(allocationStatus as Receipt['allocation_status']),
        });
    }

    // Responsible user
    if (filterValues?.responsible_user_id) {
        const userId = Number(filterValues.responsible_user_id);
        const user = users.find(u => u.id === userId);
        const label = user?.name || user?.email || `User #${userId}`;
        chips.push({
            id: 'responsible_user_id',
            label,
            color: 'primary',
        });
    }

    // Date range
    const dateLabel = quickRangeLabel(filterValues || {}, quickRangeOptions);
    if (dateLabel) {
        chips.push({
            id: 'date',
            label: dateLabel,
            color: 'info',
        });
    }

    return chips;
};

/**
 * DailyReceiptsFilters: the main filter accordion for the Daily receipts list.
 */
export const DailyReceiptsFilters = ({
    users,
}: {
    users: ResponsibleUser[];
}) => {
    const { filterValues, setFilters } = useListContext<Receipt>();
    const [search, setSearch] = useState(filterValues?.q ?? '');
    const filterValuesRef = useRef(filterValues);

    // Update ref every render so debounce callback has latest filters
    useEffect(() => {
        filterValuesRef.current = filterValues;
    }, [filterValues]);

    // Sync search field when filterValues.q changes externally (e.g., Clear filters)
    useEffect(() => {
        const currentQ = (filterValues?.q ?? '') as string;
        if (currentQ !== search.trim()) {
            setSearch(currentQ);
        }
    }, [filterValues?.q]);

    // Debounced search: after 400ms of no typing, update filters
    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmed = search.trim();
            const currentQ = (filterValuesRef.current.q ?? '') as string;

            // Only update if the value actually changed
            if (trimmed === currentQ) return;

            const next = { ...filterValuesRef.current };
            if (trimmed) {
                next.q = trimmed;
            } else {
                delete next.q;
            }
            setFilters(next, undefined, false);
        }, 400);

        return () => clearTimeout(timer);
    }, [search, setFilters]);

    const quickRangeOptions = useMemo(
        () =>
            QUICK_RANGES.map(({ range, label }) => ({
                key: range,
                label,
                value: quickDateRange(range),
            })),
        []
    );

    const activeChips = useMemo(
        () => buildDailyActiveFilterChips(filterValues, users, quickRangeOptions),
        [filterValues, users, quickRangeOptions]
    );

    const handleReceiptStatusChange = (
        _event: React.MouseEvent<HTMLElement>,
        newValue: string | null
    ) => {
        if (newValue === null) return;

        const next = { ...filterValues };
        if (newValue === 'all') {
            delete next.status;
        } else {
            next.status = newValue;
        }
        setFilters(next, undefined, false);
    };

    const handleAllocationStatusChange = (
        _event: React.MouseEvent<HTMLElement>,
        newValue: string | null
    ) => {
        if (newValue === null) return;

        const next = { ...filterValues };
        if (newValue === 'all') {
            delete next.allocation_status;
        } else {
            next.allocation_status = newValue;
        }
        setFilters(next, undefined, false);
    };

    const handleEmployeeChange = (_event: any, value: ResponsibleUser | null) => {
        const next = { ...filterValues };
        if (value === null) {
            delete next.responsible_user_id;
        } else {
            next.responsible_user_id = value.id;
        }
        setFilters(next, undefined, false);
    };

    const handleQuickRange = (value: {
        transaction_date_gte: string;
        transaction_date_lte: string;
    }) => {
        setFilters({ ...filterValues, ...value }, undefined, false);
    };

    const receiptStatusValue = (filterValues?.status as string) ?? 'all';
    const allocationStatusValue = (filterValues?.allocation_status as string) ?? 'all';
    const selectedEmployee =
        users.find(u => u.id === Number(filterValues?.responsible_user_id)) ?? null;

    return (
        <ReceiptFiltersAccordion
            idPrefix="daily-receipts"
            chips={activeChips}
            clearButton={<ClearFiltersButton stickyKeys={DAILY_STICKY_KEYS} />}
        >
            <FilterSection
                title="Search"
                legend="Match merchant name or file name."
            >
                <TextField
                    size="small"
                    fullWidth
                    type="search"
                    placeholder="Search merchant or description…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                />
            </FilterSection>

            <FilterSection
                title="Receipt status"
                legend="Whether the upload is linked to a card charge, and whether amount/date agree."
            >
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={receiptStatusValue}
                    onChange={handleReceiptStatusChange}
                    aria-label="Receipt status"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    {RECEIPT_STATUS_OPTIONS.map(([value, label]) => (
                        <ToggleButton key={value} value={value}>
                            {label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </FilterSection>

            <FilterSection
                title="Allocation status"
                legend="Bookkeeper review state of the project/category allocation."
            >
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={allocationStatusValue}
                    onChange={handleAllocationStatusChange}
                    aria-label="Allocation status"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    {ALLOCATION_STATUS_OPTIONS.map(([value, label]) => (
                        <ToggleButton key={value} value={value}>
                            {label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </FilterSection>

            <FilterSection
                title="Responsible employee"
                legend="Filter receipts by the employee responsible for them."
            >
                <Autocomplete
                    size="small"
                    options={users}
                    getOptionLabel={u => u.name || u.email}
                    value={selectedEmployee}
                    onChange={handleEmployeeChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                        <TextField {...params} label="Employee" />
                    )}
                    sx={{ maxWidth: 360 }}
                />
            </FilterSection>

            <FilterSection
                title="Transaction date"
                legend="Jump to a common period, or set an exact From/To range. Changing From/To replaces a quick period."
            >
                <Stack spacing={1.5}>
                    <QuickRangeButtons
                        ranges={quickRangeOptions}
                        filterValues={filterValues || {}}
                        onSelect={handleQuickRange}
                    />
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 0.5 }}
                        >
                            Custom range
                        </Typography>
                        <ListToolbar
                            filters={receiptDateRangeFilters}
                            actions={false}
                        />
                    </Box>
                </Stack>
            </FilterSection>
        </ReceiptFiltersAccordion>
    );
};
